import { memo, useCallback, useMemo, useRef } from 'react';
import { View, ListRenderItem } from 'react-native';
import {
  List,
  FlatList,
  PressableIcon,
  useBreakpointProp,
} from '@bonanza-social/moonlight';

import { GamesProps } from './Games.types';
import Game from './Game/Game';
import { GAME_IMAGE_SIZES } from './Game/Game.content';
import {
  CONTAINER_STYLE,
  LEFT_ARROW_CONTAINER_STYLE,
  RIGHT_ARROW_CONTAINER_STYLE,
} from './Games.content';
import { useListNavigation } from '@bonanza-social/navigation';

function Games({
  ids,
  imageType,
  paddingLeft = 'space-400',
  renderHeader,
}: GamesProps) {
  const showArrows = useBreakpointProp({
    mobile: false,
    tablet: false,
    desktop: true,
  });

  const style = useMemo(
    () =>
      ({
        paddingLeft,
        paddingTop: 'space-200',
        paddingBottom: 'space-100',
        alignItems: 'flex-start',
      }) as const,
    [paddingLeft],
  );

  const flatListRef = useRef<FlatList<(typeof ids)[number]>>(null);
  const containerRef = useRef<View>(null);

  const {
    handleScroll,
    handleLayout,
    handleContentSizeChange,
    showLeftArrow,
    showRightArrow,
    isLeftArrowHovered,
    isRightArrowHovered,
    handleArrowForward,
    handleArrowBack,
    handleLeftArrowMouseEnter,
    handleLeftArrowMouseLeave,
    handleRightArrowMouseEnter,
    handleRightArrowMouseLeave,
  } = useListNavigation({
    listRef: flatListRef,
    containerRef,
    itemWidth: GAME_IMAGE_SIZES[imageType].width,
    showArrows,
    itemsCount: ids.length,
    enableDragging: true,
    enableArrows: true,
  });

  const renderGame = useCallback<ListRenderItem<(typeof ids)[number]>>(
    ({ item }) => (
      <Game id={item} marginRight="space-300" imageType={imageType} />
    ),
    [imageType],
  );

  return (
    <View ref={containerRef} style={CONTAINER_STYLE} onLayout={handleLayout}>
      <List
        ref={flatListRef}
        data={ids}
        renderItem={renderGame}
        contentContainerStyle={style}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        ListHeaderComponent={renderHeader}
        scrollEventThrottle={16}
        windowSize={2}
        showsHorizontalScrollIndicator={false}
        horizontal
      />
      {showArrows && showLeftArrow && (
        <View
          style={LEFT_ARROW_CONTAINER_STYLE}
          // @ts-expect-error - This is intentional for React Native Web
          onMouseEnter={handleLeftArrowMouseEnter}
          onMouseLeave={handleLeftArrowMouseLeave}
        >
          <PressableIcon
            name="arrowBack"
            alignItems="center"
            justifyContent="center"
            backgroundColor={
              isLeftArrowHovered
                ? 'primarySolidBackground'
                : 'primaryBackground'
            }
            borderRadius="radius-200"
            iconHeight="space-500"
            iconWidth="space-500"
            width="space-1000"
            height="space-1000"
            gradient={isLeftArrowHovered ? undefined : 'accentBackground'}
            onPress={handleArrowBack}
          />
        </View>
      )}
      {showArrows && showRightArrow && (
        <View
          style={RIGHT_ARROW_CONTAINER_STYLE}
          // @ts-expect-error - This is intentional for React Native Web
          onMouseEnter={handleRightArrowMouseEnter}
          onMouseLeave={handleRightArrowMouseLeave}
        >
          <PressableIcon
            name="arrowForwardOutlined"
            alignItems="center"
            justifyContent="center"
            backgroundColor={
              isRightArrowHovered
                ? 'primarySolidBackground'
                : 'primaryBackground'
            }
            borderRadius="radius-200"
            width="space-1000"
            height="space-1000"
            gradient={isRightArrowHovered ? undefined : 'accentBackground'}
            onPress={handleArrowForward}
          />
        </View>
      )}
    </View>
  );
}

export default memo(Games);
